import {
  LitElement,
  classMap,
  createRef,
  css,
  html,
  nothing,
  ref,
  when,
  unsafeHTML,
} from '@lit'
import * as paths from '@nova/paths'
import styles from '@nova/styles'
import { viewportObserver } from '@nova/utils'
import '@vendor/media-chrome'
import '@vendor/media-chrome-vimeo'
import '@vendor/media-chrome-youtube'

class NovaAdaptiveVideo extends LitElement {
  static properties = {
    sectionId: {
      type: String,
      attribute: 'section-id',
    },
    srcDesktop: {
      type: String,
      attribute: 'src-desktop',
    },
    aspectRatioDesktop: {
      type: String,
      attribute: 'aspect-ratio-desktop',
    },
    srcMobile: {
      type: String,
      attribute: 'src-mobile',
    },
    aspectRatioMobile: {
      type: String,
      attribute: 'aspect-ratio-mobile',
    },
    srcType: {
      type: String,
      attribute: 'src-type',
    },
    posterDesktop: {
      type: String,
      attribute: 'poster-desktop',
    },
    posterMobile: {
      type: String,
      attribute: 'poster-mobile',
    },
    descriptionDesktop: {
      type: String,
      attribute: 'description-desktop',
    },
    descriptionMobile: {
      type: String,
      attribute: 'description-mobile',
    },
    loadingType: {
      type: String,
      attribute: 'loading',
    },
    viewport: {
      type: String,
      state: true,
    },
    autoplay: {
      type: Boolean,
    },
    loop: {
      type: Boolean,
    },
    sound: {
      type: Boolean,
    },
    controls: {
      type: Boolean,
    },
    captionsFile: {
      type: String,
      attribute: 'captions-file',
    },
    captionsColor: {
      type: String,
      attribute: 'captions-color',
    },
    trackProgress: {
      type: Boolean,
      attribute: 'track-progress',
    },
    background: {
      type: Boolean,
      attribute: 'background',
    },
    adaptContainerHeight: {
      type: Boolean,
      attribute: 'adapt-container-height',
    },
    adaptContainerAspectRatio: {
      type: Boolean,
      attribute: 'adapt-container-aspect-ratio',
    },
    videoContent: {
      type: String,
      attribute: 'video-content',
    },
    videoDisclaimer: {
      type: String,
      attribute: 'video-disclaimer',
    },
    borderRadius: {
      type: String,
      attribute: 'border-radius',
    },
  }

  get activeSrc() {
    // Always fallback on desktop asset if no mobile asset is provided
    if (!this.srcMobile) return this.srcDesktop
    // Return mobile asset if current viewport isn't desktop, else return desktop asset
    return ['mobile', 'tablet'].includes(this.viewport)
      ? this.srcMobile
      : this.srcDesktop
  }

  get activeAspectRatio() {
    // Always fallback on desktop asset if no mobile asset is provided
    if (!this.aspectRatioMobile) return this.aspectRatioDesktop
    // Return mobile asset if current viewport isn't desktop, else return desktop asset
    return ['mobile', 'tablet'].includes(this.viewport)
      ? this.aspectRatioMobile
      : this.aspectRatioDesktop
  }

  get activeDesc() {
    return this.activeSrc === this.srcMobile &&
      this.descriptionMobile !== 'false'
      ? this.descriptionMobile
      : this.activeSrc === this.srcDesktop &&
          this.descriptionDesktop !== 'false'
        ? this.descriptionDesktop
        : ''
  }

  get activePoster() {
    return this.activeSrc === this.srcMobile && this.posterMobile !== 'false'
      ? this.posterMobile
      : this.activeSrc === this.srcDesktop && this.posterDesktop !== 'false'
        ? this.posterDesktop
        : false
  }

  constructor() {
    super()
    this.viewport = viewportObserver.currentViewport
    viewportObserver.addListener(this._handleViewportChange)
    this.srcMobile = false
    this.srcDesktop = false
    this.aspectRatioMobile = false
    this.aspectRatioDesktop = '16/9'
    this.videoRef = createRef()
    this.loadingType = 'lazy'
    this.background = false
    this.adaptContainerHeight = false
    this.adaptContainerAspectRatio = false
    this.borderRadius = '0'
    this.captionsFile = ''
    this.captionsColor = ''
    this.currentCaption = ''
  }

  connectedCallback() {
    super.connectedCallback()

    if (this.autoplay) this.autoplaying = true
    this.percentageCompleted = 0
    this.videoTwentyFive = false
    this.videoFifty = false
    this.videoSeventyFive = false
    this.videoComplete = false
    this.videoInView = false
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    if (this.$video && this._handleCaptionUpdate) {
      this.$video.removeEventListener('timeupdate', this._handleCaptionUpdate)
    }
  }

  firstUpdated() {
    this.$video = this.videoRef.value
    if (this.captionsFile) {
      this.loadCaptions()
    }
    this.setupCaptionListener()
  }

  setupCaptionListener() {
    if (this.captionsFile) {
      this.$video.addEventListener('timeupdate', this._handleCaptionUpdate)
    }
  }

  async loadCaptions() {
    try {
      const response = await fetch(this.captionsFile)
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      const vttText = await response.text()
      this.parseVTT(vttText)
    } catch (error) {
      console.error('[Video Captions] Failed to load captions:', error)
    }
  }

  parseVTT(vttText) {
    const cues = []
    const lines = vttText.split('\n')
    let currentCue = null

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim()

      if (line === 'WEBVTT' || line === '' || line.startsWith('NOTE')) {
        continue
      }

      if (line.includes('-->')) {
        const [start, end] = line.split('-->').map((time) => this.parseVTTTime(time.trim()))
        if (currentCue) {
          cues.push({
            start: currentCue.start,
            end: currentCue.end,
            text: currentCue.text.trim(),
          })
        }
        currentCue = { start, end, text: '' }
      } else if (currentCue && line) {
        currentCue.text += (currentCue.text ? ' ' : '') + line
      }
    }

    if (currentCue) {
      cues.push({
        start: currentCue.start,
        end: currentCue.end,
        text: currentCue.text.trim(),
      })
    }

    this.captionCues = cues
  }

  parseVTTTime(timeStr) {
    const parts = timeStr.split(':')
    const secondsParts = parts[2].split('.')
    const hours = Number.parseInt(parts[0], 10) || 0
    const minutes = Number.parseInt(parts[1], 10) || 0
    const seconds = Number.parseInt(secondsParts[0], 10) || 0
    const milliseconds = Number.parseInt(secondsParts[1], 10) || 0

    return hours * 3600 + minutes * 60 + seconds + milliseconds / 1000
  }

  _handleCaptionUpdate = () => {
    if (!this.$video || !this.captionCues) return

    let currentTime = 0
    if (this.$video.currentTime !== undefined) {
      currentTime = this.$video.currentTime
    } else if (this.$video.getCurrentTime) {
      currentTime = this.$video.getCurrentTime()
    } else if (this.$video.shadowRoot) {
      const iframe = this.$video.shadowRoot.querySelector('iframe')
      if (iframe?.contentWindow) {
        console.error('[Video Captions] YouTube/Vimeo video detected, time tracking may require special handling')
      }
    }

    if (!currentTime && this.captionCues.length > 0) {
      return
    }

    const activeCue = this.captionCues.find(
      (cue) => currentTime >= cue.start && currentTime <= cue.end,
    )

    if (activeCue && activeCue.text !== this.currentCaption) {
      this.currentCaption = activeCue.text
      this.requestUpdate()
    } else if (!activeCue && this.currentCaption) {
      this.currentCaption = ''
      this.requestUpdate()
    }
  }

  _handleViewportChange = (newViewport) => {
    this.viewport = newViewport
  }

  _handlePlay = () => {
    if (!this.autoplaying) {
      // Track play analytics event here
    }
  }

  _handlePause = () => {
    this.autoplaying = false
    // Track pause analytics event here
  }

  _handleProgress = () => {
    if (this.$video?.readyState >= 2 && this.trackProgress) {
      const progressUpdate = () => {
        this.percentageCompleted =
          (this.$video.currentTime / this.$video.duration) * 100
        if (!this.videoTwentyFive && this.percentageCompleted > 25) {
          this.videoTwentyFive = true
          this._handleTrackProgress('25%')
        }
        if (!this.videoFifty && this.percentageCompleted > 50) {
          this.videoFifty = true
          this._handleTrackProgress('50%')
        }
        if (!this.videoSeventyFive && this.percentageCompleted > 75) {
          this.videoSeventyFive = true
          this._handleTrackProgress('75%')
        }
        if (!this.videoComplete && this.percentageCompleted > 99) {
          this.videoComplete = true
          this._handleTrackProgress('100%')
          this.$video.removeEventListener('timeupdate', progressUpdate)
        }
      }
      this.$video.addEventListener('timeupdate', progressUpdate)
    }
  }

  _handleTrackProgress = (progress) => {
    // Track progress analytics event here
  }

  renderYoutubeVideo = () => {
    return html`
      <youtube-video
        ${ref(this.videoRef)}
        part="video-element"
        id="video-element"
        slot="media"
        src=${this.activeSrc}
        loading=${this.loadingType}
        muted
        playsinline
        aria-label=${this.activeDesc}
        ?autoplay=${this.autoplay}
        ?loop=${this.loop}
        @loadeddata=${this._handleProgress}
        @pause=${this._handlePause}
        @play=${this._handlePlay}
      >
        ${this.renderCaptions()}
      </youtube-video>
    `
  }

  renderVimeoVideo = () => {
    return html`
      <vimeo-video
        ${ref(this.videoRef)}
        part="video-element"
        id="video-element"
        slot="media"
        src=${this.activeSrc}
        loading=${this.loadingType}
        muted
        playsinline
        aria-label=${this.activeDesc}
        ?autoplay=${this.autoplay}
        ?loop=${this.loop}
        @loadeddata=${this._handleProgress}
        @pause=${this._handlePause}
        @play=${this._handlePlay}
      >
        ${this.renderCaptions()}
      </vimeo-video>
    `
  }

  renderUploadedVideo = () => {
    return html`
      <video
        ${ref(this.videoRef)}
        part="video-element"
        id="video-element"
        slot="media"
        src=${this.activeSrc}
        loading=${this.loadingType}
        muted
        playsinline
        aria-label=${this.activeDesc}
        ?autoplay=${this.autoplay}
        ?loop=${this.loop}
        @loadeddata=${this._handleProgress}
        @pause=${this._handlePause}
        @play=${this._handlePlay}
      >
        ${this.renderCaptions()}
      </video>
    `
  }

  renderCaptions = () => {
    if (this.captionsFile) {
      return html`
        <track
          label="English"
          kind="subtitles"
          srclang="en"
          src="${this.captionsFile}"
        />`
    }
  }

  renderControls = () => {
    return html`
      <media-control-bar part="controls">
        ${when(
          this.videoContent,
          () => html`
            <div class="hidden block--md block--lg video-player__content-wrapper">
              ${unsafeHTML(this.videoContent)}
            </div>
          `,
          () => nothing,
        )}
        <media-play-button>
          <span slot="play">${paths.play}</span>
          <span slot="pause">${paths.pause}</span>
        </media-play-button>
        ${when(
          this.sound,
          () => html`
            <media-mute-button>
              <span slot="high">${paths.audioOn}</span>
              <span slot="off">${paths.audioOff}</span>
            </media-mute-button>
          `,
          () => nothing,
        )}
        ${when(
          this.captionsFile,
          () => html`
            <media-captions-button>
              <span slot="on">${paths.captionsOn}</span>
              <span slot="off">${paths.captionsOff}</span>
            </media-captions-button>
          `,
          () => nothing,
        )}
        ${when(
          this.videoDisclaimer,
          () => html`
            <aside aria-label="Legal disclaimer" role="note" class="hidden block--md block--lg video-player__disclaimer">
              ${unsafeHTML(this.videoDisclaimer)}
            </aside>
          `,
          () => nothing,
        )}
      </media-control-bar>
    `
  }

  play() {
    this.$video.play()
  }

  render() {
    const stateClasses = {
      'background-video': this.background,
      'adapter-height': this.adaptContainerHeight,
      'adapter-aspect-ratio': this.adaptContainerAspectRatio,
    }

    return html`
      <media-controller class="${classMap(
        stateClasses,
      )}" part="media-controller" style="--active-aspect-ratio: ${
        this.activeAspectRatio
      }; --border-radius: ${this.borderRadius}">
        ${when(
          this.srcType === 'youtube',
          this.renderYoutubeVideo,
          () => nothing,
        )}
        ${when(this.srcType === 'vimeo', this.renderVimeoVideo, () => nothing)}
        ${when(
          this.srcType === 'upload',
          this.renderUploadedVideo,
          () => nothing,
        )}
        ${when(
          this.activePoster,
          () => html`
              <media-poster-image
                slot="poster"
                src=${this.activePoster}>
              </media-poster-image>`,
          () => nothing,
        )}
        ${when(this.controls, this.renderControls, () => nothing)}
      </media-controller>
      ${when(
        this.captionsFile,
        () => html`
          <div
            class="video-captions"
            part="captions"
            style="--captions-color: ${this.captionsColor || 'var(--nova-color-text)'}"
          >
            ${this.currentCaption || ''}
          </div>
        `,
        () => nothing,
      )}
    `
  }

  static styles = [
    styles.a11y,
    styles.variables,
    css`
      :host {
        display: block;
        height: 100%;
        width: 100%;
        position: relative;
      }
      #video-element {
        height: 100%;
        object-fit: contain;
        width: 100%;
        border-radius: var(--border-radius);
      }
      media-controller {
        width: 100%;
        height: auto;
        aspect-ratio: var(--active-aspect-ratio);
        display: block;
        border-radius: var(--border-radius);
      }
      .adapter-height {
        width: auto;
        height: 100%;
        left: 50%;
        transform: translateX(-50%);
      }
      .adapter-aspect-ratio {
        width: 100%;
        height: 100%;
      }
      .adapter-aspect-ratio #video-element {
        object-fit: cover;
      }
      media-control-bar {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        column-gap: 15px;
        row-gap: 30px;
        flex-wrap: wrap;
        justify-content: center;
      }
      media-play-button,
      media-mute-button {
        display: flex;
        width: 50px;
        height: 50px;
        align-items: center;
        justify-content: center;
        background: var(--media-bg-color, #fff);
        color: var(--media-text-reverse-color, #272726);
        border-radius: 9999px;
      }
      media-play-button > *,
      media-mute-button > * {
        display: flex;
      }
      @media screen and (max-width: 768px) {
        .background-video {
          height: 100%;
          width: auto;
          left: 50%;
          transform: translateX(-50%);
        }
      }

      .video-player__content-wrapper {
        width: 100%;
      }

      .video-player__content {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        word-wrap: break-word;

        & ol,
        & ul {
          padding-left: 1.5em;
          text-align: left;
        }

        & h2 {
          max-width: 37.5rem;
          margin-block-start: 0;
          margin-block-end: 0;

          &.h1 {
            font: var(--nova-h1-font);
            letter-spacing: var(--nova-h1-letter-spacing);
            text-transform: var(--nova-h1-text-transform);
          }
          &.h2 {
            font: var(--nova-h2-font);
            letter-spacing: var(--nova-h2-letter-spacing);
            text-transform: var(--nova-h2-text-transform);
          }
          &.h3 {
            font: var(--nova-h3-font);
            letter-spacing: var(--nova-h3-letter-spacing);
            text-transform: var(--nova-h3-text-transform);
          }
          &.h4 {
            font: var(--nova-h4-font);
            letter-spacing: var(--nova-h4-letter-spacing);
            text-transform: var(--nova-h4-text-transform);
          }
        }

        & em {
          font-family: var(--nova-font-italic-primary);
        }

        & p,
        & li {
          font: var(--nova-body-font);
          letter-spacing: var(--nova-body-letter-spacing);
          max-width: 445px;
          margin: auto;
        }
      }
      .video-player__content--logo {
        margin-bottom: 30px;

        & img {
          max-width: 150px;
          height: auto;
        }
      }

      .video-player__disclaimer {
        text-align: center;
        word-wrap: break-word;
        font: var(--nova-body-caption);
	      letter-spacing: var(--nova-caption-letter-spacing);
        width: 100%;

        & a {
          color: inherit;
          text-underline-offset: 2px;
        }

        & strong {
          font-family: var(--nova-font-sans-bold);
        }
      }

      @media screen and (min-width: 750px) and (max-width: 1023px) {
        .hidden--md {
          display: none !important;
        }
        .block--md {
          display: block !important;
        }
        .flex--md {
          display: flex !important;
        }
      }

      @media screen and (min-width: 1024px) {
        .hidden--lg {
          display: none !important;
        }
        .block--lg {
          display: block !important;
        }
        .flex--lg {
          display: flex !important;
        }
      }

      media-poster-image::after {
        content: '';
        position: absolute;
        inset: 0;
        background: var(--background);
      }

      /* HACK: For main font family to correctly position the ® symbol */
      .superscript,
      .video-player__disclaimer em .superscript {
        font-size: 50% !important;
        vertical-align: super;
      }

      em .superscript {
        font-size: inherit !important;
        vertical-align: baseline;
        font-style: italic;
      }

      .video-captions {
        position: absolute;
        bottom: var(--nova-space-md);
        left: var(--nova-space-md);
        right: var(--nova-space-md);
        text-align: center;
        font: var(--nova-body-font);
        color: var(--captions-color, var(--nova-color-text));
        display: flex;
        align-items: center;
        justify-content: center;
      }
    `,
  ]
}

customElements.define('nova-adaptive-video', NovaAdaptiveVideo)
